/**
 * useFormField - Shared hook for dual-mode form field handling
 *
 * Enables components to work in two modes:
 * 1. Props mode (legacy): Uses passed-in value and setter
 * 2. Context mode (new): Uses React Hook Form context via FormProvider
 *
 * Automatically detects which mode to use based on presence of prop setter.
 * If a setter prop is provided, uses props mode for backward compatibility.
 * If no setter prop is provided and FormProvider context exists, uses context mode.
 *
 * In context mode, uses local state for immediate controlled-input updates
 * to avoid React's restoreControlledState overwriting user input before
 * RHF's watch subscription re-renders the component.
 */

import { useCallback, useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

/**
 * Custom hook for dual-mode form field handling
 *
 * @param {string} fieldPath - Path to the field in form context (e.g., 'solar.enabled')
 * @param {*} propValue - Value from props (legacy mode)
 * @param {Function} propSetter - Setter function from props (legacy mode)
 * @returns {Object} - { value, setValue, error }
 */
export function useFormField(fieldPath, propValue, propSetter) {
  // Try to get form context (will be null if not in FormProvider)
  let formContext = null;
  try {
    formContext = useFormContext();
  } catch (e) {
    // Not in a FormProvider, use props
  }

  // If we have a prop setter, use props mode (backward compatible)
  const useContextMode = formContext && !propSetter;

  // In context mode, read RHF's current value for external sync
  const rhfValue = useContextMode ? formContext?.watch(fieldPath) : undefined;

  // Local state provides immediate updates for controlled inputs.
  // Without this, RHF's watch value lags behind setValue, causing
  // React's restoreControlledState to revert user keystrokes.
  const [localValue, setLocalValue] = useState(rhfValue);

  // Sync RHF → local when RHF value changes externally (e.g., form reset, load)
  useEffect(() => {
    if (useContextMode && rhfValue !== undefined) {
      setLocalValue(rhfValue);
    }
  }, [useContextMode, rhfValue]);

  const value = useContextMode ? localValue : propValue;

  const setValue = useCallback((newValue) => {
    if (useContextMode && formContext) {
      // Update local state immediately for responsive controlled input
      setLocalValue(newValue);
      // Then sync to RHF store
      formContext.setValue(fieldPath, newValue, { shouldDirty: true });
    } else if (propSetter) {
      propSetter(newValue);
    }
  }, [useContextMode, formContext, fieldPath, propSetter]);

  // Get nested error from form state
  const getNestedError = (errors, path) => {
    const parts = path.split('.');
    let current = errors;
    for (const part of parts) {
      if (!current) return null;
      current = current[part];
    }
    return current;
  };

  const error = useContextMode
    ? getNestedError(formContext?.formState?.errors, fieldPath)
    : null;

  return { value: value ?? '', setValue, error };
}

/**
 * Hook for handling array fields in dual mode
 *
 * @param {string} fieldPath - Path to the array field (e.g., 'battery.batteries')
 * @param {Array} propValue - Array value from props
 * @param {Function} propSetter - Setter for the array from props
 * @returns {Object} - { value, setValue, append, remove, update }
 */
export function useFormArrayField(fieldPath, propValue, propSetter) {
  let formContext = null;
  try {
    formContext = useFormContext();
  } catch (e) {
    // Not in a FormProvider
  }

  const useContextMode = formContext && !propSetter;

  const value = useContextMode
    ? formContext?.watch(fieldPath) || []
    : propValue || [];

  const setValue = useCallback((newValue) => {
    if (useContextMode && formContext) {
      formContext.setValue(fieldPath, newValue, { shouldDirty: true });
    } else if (propSetter) {
      propSetter(newValue);
    }
  }, [useContextMode, formContext, fieldPath, propSetter]);

  const append = useCallback((item) => {
    setValue([...value, item]);
  }, [value, setValue]);

  const remove = useCallback((id) => {
    setValue(value.filter(item => item.id !== id));
  }, [value, setValue]);

  const update = useCallback((id, field, fieldValue) => {
    setValue(value.map(item =>
      item.id === id ? { ...item, [field]: fieldValue } : item
    ));
  }, [value, setValue]);

  return { value, setValue, append, remove, update };
}

export default useFormField;
